import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Save, Search, Shield, Users, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { eventService } from '@/lib/api/event.service';
import { teamMemberService } from '@/lib/api/teamMember.service';
import { UserService } from '@/lib/api/user.service';
import type { UserContact } from '@/lib/api/user.service';
import type { CreateEventMemberDto, EventMemberRole } from '@/types/event.types';
import type { TeamMemberWithUser } from '@/types/teamMember.types';

interface SelectableUser {
  id: number;          // user id
  full_name: string;
  email?: string;
  isTeamMember: boolean;
  teamRole?: string;
}

export const AddEventMember: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['events', 'common']);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<SelectableUser[]>([]);
  const [existingMemberIds, setExistingMemberIds] = useState<Set<number>>(new Set());
  const [selectedUser, setSelectedUser] = useState<SelectableUser | null>(null);
  const [formData, setFormData] = useState<CreateEventMemberDto>({
    user_id: 0,
    role: 'member' as EventMemberRole,
    permissions: []
  });

  const eventId = parseInt(id || '0');

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);
      try {
        // Fetch event, existing event members, and all users in parallel
        const eventData = await eventService.getEvent(eventId);
        
        const [existingMembers, allUsers, teamMembers] = await Promise.all([
          eventService.getEventMembers(eventId).catch(() => []),
          UserService.getAllUsers().catch(() => []),
          eventData.celebration_id
            ? teamMemberService.getTeamMembers(eventData.celebration_id).catch(() => [])
            : Promise.resolve([])
        ]);

        // Track who's already an event member
        const existingIds = new Set(existingMembers.map((m: any) => m.user_id));
        setExistingMemberIds(existingIds);

        // Build team member lookup by user_id
        const teamMemberMap = new Map<number, TeamMemberWithUser>();
        teamMembers.forEach((tm: TeamMemberWithUser) => {
          teamMemberMap.set(tm.user_id, tm);
        });

        // Merge users with team member info
        const selectableUsers: SelectableUser[] = allUsers.map((u: UserContact) => {
          const tm = teamMemberMap.get(u.user_id || u.id);
          return {
            id: u.user_id || u.id,
            full_name: u.full_name || 'Unknown',
            email: u.email,
            isTeamMember: !!tm,
            teamRole: tm?.role
          };
        });

        // Sort: team members first, then alphabetical
        selectableUsers.sort((a, b) => {
          if (a.isTeamMember && !b.isTeamMember) return -1;
          if (!a.isTeamMember && b.isTeamMember) return 1;
          return a.full_name.localeCompare(b.full_name);
        });

        setUsers(selectableUsers);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(t('events:member.errors.failedLoad'));
      } finally {
        setDataLoading(false);
      }
    };

    if (eventId) {
      fetchData();
    }
  }, [eventId]);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(
      u => u.full_name.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  const handleSelectUser = (user: SelectableUser) => {
    setSelectedUser(user);
    setError(null);

    let defaultRole: EventMemberRole = 'member';
    let defaultPermissions: string[] = [];

    if (user.isTeamMember) {
      if (user.teamRole === 'organizer') {
        defaultRole = 'organizer';
      } else if (user.teamRole === 'event_manager') {
        defaultRole = 'event_manager';
        defaultPermissions = ['manage_tasks', 'edit_event'];
      }
    }

    setFormData({
      user_id: user.id,
      role: defaultRole,
      permissions: defaultPermissions
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUser) {
      setError('Please select a user to add to this event.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await eventService.addEventMember(eventId, formData);
      navigate(`/events/${eventId}`);
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('events:member.errors.failedAdd'));
      }
      console.error('Error adding member:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'organizer': return t('events:member.rolePermissions.roles.organizer');
      case 'event_manager': return t('events:member.rolePermissions.roles.event_manager');
      case 'co_organizer': return t('events:member.rolePermissions.roles.event_manager');
      case 'vendor_coordinator': return t('events:member.rolePermissions.roles.vendor_coordinator');
      case 'member': return t('events:member.rolePermissions.roles.member');
      default: return role;
    }
  };

  const shouldShowPermissions = selectedUser && formData.role !== 'organizer';

  return (
    <PageContainer width="default" className="space-y-6">
      <AppBreadcrumbs />
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('events:member.addTitle')}</h1>
        <p className="text-sm text-gray-500">{t('events:member.addSubtitle')}</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* User Selection */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" />
              {t('events:member.selectUser.title')}
            </CardTitle>
            <CardDescription>
              {t('events:member.selectUser.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('events:member.selectUser.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* User list */}
            <div className="border rounded-lg max-h-64 overflow-y-auto">
              {dataLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  {t('events:member.selectUser.loading')}
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  {searchQuery ? t('events:member.selectUser.noMatchSearch') : t('events:member.selectUser.noUsers')}
                </div>
              ) : (
                filteredUsers.map((user) => {
                  const isExisting = existingMemberIds.has(user.id);
                  const isSelected = selectedUser?.id === user.id;

                  return (
                    <button
                      key={user.id}
                      type="button"
                      disabled={isExisting}
                      onClick={() => handleSelectUser(user)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b last:border-b-0 transition-colors
                        ${isExisting
                          ? 'opacity-50 cursor-not-allowed bg-gray-50'
                          : isSelected
                            ? 'bg-primary/10 border-l-2 border-l-primary'
                            : 'hover:bg-muted/50 cursor-pointer'
                        }`}
                    >
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold
                        ${isSelected ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                        {user.full_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{user.full_name}</span>
                          {user.isTeamMember && (
                            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                              {getRoleLabel(user.teamRole || '')}
                            </span>
                          )}
                          {isExisting && (
                            <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">
                              {t('events:member.selectUser.alreadyAdded')}
                            </span>
                          )}
                        </div>
                        {user.email && (
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Role & Permissions - show after user is selected */}
        {selectedUser && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Shield className="h-4 w-4" />
                {t('events:member.rolePermissions.title')}
              </CardTitle>
              <CardDescription>
                {t('events:member.rolePermissions.description', { name: selectedUser.full_name })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Role selection */}
              <div className="space-y-2">
                <Label htmlFor="role">{t('events:member.rolePermissions.roleLabel')}</Label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    role: e.target.value as EventMemberRole,
                    permissions: e.target.value === 'organizer' ? [] : prev.permissions
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="member">{t('events:member.rolePermissions.roles.member')}</option>
                  <option value="event_manager">{t('events:member.rolePermissions.roles.event_manager')}</option>
                  <option value="organizer">{t('events:member.rolePermissions.roles.organizer')}</option>
                </select>
              </div>

              {/* Permissions */}
              {shouldShowPermissions && (
                <div className="space-y-2">
                  <Label>{t('events:member.rolePermissions.permissionsLabel')}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'manage_guests', label: t('events:member.rolePermissions.permissions.manage_guests') },
                      { key: 'manage_tasks', label: t('events:member.rolePermissions.permissions.manage_tasks') },
                      { key: 'edit_event', label: t('events:member.rolePermissions.permissions.edit_event') },
                      { key: 'manage_activities', label: t('events:member.rolePermissions.permissions.manage_activities') }
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.permissions?.includes(key) || false}
                          onChange={(e) => {
                            const permissions = formData.permissions || [];
                            setFormData(prev => ({
                              ...prev,
                              permissions: e.target.checked
                                ? [...permissions, key]
                                : permissions.filter(p => p !== key)
                            }));
                          }}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <span className="text-sm">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Form Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/events/${eventId}`)}
            disabled={loading}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={loading || !selectedUser}>
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            <Save className="h-4 w-4 mr-2" />
            {t('events:member.buttons.addMember')}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
};
